import { BuilderContent } from "@builder.io/sdk";
import { useTranslation } from "next-i18next";
import React from "react";

import { GIFT_CARD_LOCALES_IN_PROD, GIFT_CARD_LOCALES_IN_NONPROD } from "@/shared/constants";

import { parallel } from "@/utils/promises";
import { isProd } from "@/utils/runtime-environment";
import serverSideTranslations from "@/utils/serverSideTranslations";

import { init } from "@/builder";
import { BuilderModel } from "@/builder/types";

import GiftCardImageGridHeader from "@/components/gift-card-image-grid-header/gift-card-image-grid-header";
import { GiftCardImageGrid } from "@/components/gift-card-image-grid/gift-card-image-grid";
import { GiftCardHeroBanner } from "@/components/gift-cards/gift-card-hero-banner/gift-card-hero-banner";

import { GiftCardProps } from "@/interfaces/gift-cards/gift-card-interface";
import { ChunkedNav, GenericNavItem } from "@/interfaces/navigation-interface";

import { Locale } from "@/types/locale";
import { GetServerSideProps } from "@/types/page";

import DefaultLayout from "../../layouts/default";
import { getBuilderMessages, getBuilderNavLinks } from "../api/builder/builder";
import { fetchGiftCards } from "../api/store-directory/gift-cards";
import { getFooterNavigation, getHeaderNavigation } from "../api/store-directory/navigation";

interface LocaleProps {
    locale: Locale;
}

type NavProps = {
    headerNavigationItems: ChunkedNav;
    footerLocalNavItems: ChunkedNav;
    footerRegionalNavItems: Array<Array<GenericNavItem>>;
};

type OwnProps = {
    giftCards: Array<GiftCardProps>;
    builderMessages: Array<BuilderContent>;
    builderNavLinks: Array<BuilderContent>;
};

type Props = OwnProps & NavProps & LocaleProps;

init(BuilderModel.MESSAGE);

export const getServerSideProps: GetServerSideProps<Props> = async ({ locale, resolvedUrl }) => {
    if (
        (isProd() && !GIFT_CARD_LOCALES_IN_PROD.includes(locale)) ||
        (!isProd() && !GIFT_CARD_LOCALES_IN_NONPROD.includes(locale))
    ) {
        return {
            notFound: true,
        };
    }

    const parallelProps = await parallel({
        headerNavigationItems: () => getHeaderNavigation(locale),
        footerNavItems: () => getFooterNavigation(locale),
        giftCards: () => fetchGiftCards(locale),
        builderMessages: () => getBuilderMessages(locale, { url: resolvedUrl }),
        builderNavLinks: () => getBuilderNavLinks(locale),
        _nextI18Next: () => serverSideTranslations(locale),
    });

    return {
        props: {
            locale,
            footerLocalNavItems: parallelProps.footerNavItems[0],
            footerRegionalNavItems: parallelProps.footerNavItems[1],
            ...parallelProps,
        },
    };
};

const GiftCards = ({
    locale,
    headerNavigationItems,
    footerLocalNavItems,
    footerRegionalNavItems,
    giftCards,
    builderMessages,
    builderNavLinks,
}: Props): React.ReactNode => {
    const { t } = useTranslation();
    const description = locale === "en-AU" ? t("giftCards.metaDescription") : undefined;
    return (
        <DefaultLayout
            currentLocale={locale}
            description={description}
            headerNavigationItems={headerNavigationItems}
            footerLocalNavItems={footerLocalNavItems}
            footerRegionalNavItems={footerRegionalNavItems}
            builderNavLinks={builderNavLinks}
            builderMessages={builderMessages}
        >
            <GiftCardImageGridHeader />
            <GiftCardHeroBanner locale={locale} />
            <GiftCardImageGrid giftCards={giftCards} />
        </DefaultLayout>
    );
};

export default GiftCards;
